import { CategoryOptionAPI } from '@business/api/category_option';
import { CategoryOptionResponse, CategoryOptionId } from '@business/entities/category_option';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { askChangeReason } from '@modules/common/ChangeReasonModal';
import { switchMap } from 'rxjs/operators';

export class CategoryOptionBloc extends BaseBloc {
  private readonly categoryOptionRepo = new Repository<CategoryOptionResponse>({
    getItemId: category => category.id,
  });

  readonly options$ = this.categoryOptionRepo.items$;
  readonly selectTemplate = this.categoryOptionRepo.selectItem;

  onReset() {
    this.categoryOptionRepo.reset();
  }

  all = (withTrashed = false) => {
    return CategoryOptionAPI.all(withTrashed).pipe(
      this.categoryOptionRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_category_options_error') }),
    );
  };

  fetch = (id: CategoryOptionId) => {
    return CategoryOptionAPI.fetch(id).pipe(
      this.categoryOptionRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_category_options_error') }),
    );
  };

  create = (data: Partial<CategoryOptionResponse>) => {
    return CategoryOptionAPI.create(data).pipe(
      this.categoryOptionRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_option_category'),
      }),
    );
  };

  // Edits and deletes ask for the reason first (askChangeReason, kept in the Activity Log); closing that window saves
  // nothing
  update = (id: CategoryOptionId, data: Partial<CategoryOptionResponse>) => {
    return askChangeReason().pipe(
      switchMap(changeNote => CategoryOptionAPI.update(id, data, changeNote)),
      this.categoryOptionRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_option_category'),
      }),
    );
  };

  destroy = (id: CategoryOptionId) => {
    return askChangeReason('Delete Option', 'Do you really want to remove this option?', 'Delete').pipe(
      switchMap(changeNote => CategoryOptionAPI.destroy(id, changeNote)),
      this.categoryOptionRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_option_category'),
      }),
    );
  };
}
